import type { BuildArrestReportDefinition } from './types';

export const buildArrestReportDefinition: BuildArrestReportDefinition = async ({
  ctx,
}) => {
  const moment = (await import('moment')).default;
  const { DATE_TIME_FORMAT } = await import('@/config');
  const { buildReportHeader } = await import(
    '@/utils/reports/buildReportHeader'
  );
  const { getCachedBase64Image } = await import(
    '@/pages/Kases/Components/KasePrintModal/utils/imageHelpers'
  );
  const { arrestPdfStyles: pdfStyles, arrestPageMargins: pageMargins } =
    await import('./arrestPdfStyles');
  const {
    createCard,
    createCardHeader,
    createDetailGrid,
    createDividerLine,
    draftjsRawToPdfmakeBlocks,
  } = await import('./arrestTransformHelpers');

  const { arrestReportSections } = await import('./template');
  const {
    FALLBACK,
    formatSafisLatentPrint,
    isDataUrlImage,
    narrativePlainText,
    safeText,
  } = await import('./mappers');

  // Convert logo to base64 if available (same as Case Report)
  let logo = undefined as string | undefined;
  if ((ctx.agency as any)?.logo?.url) {
    try {
      logo = await getCachedBase64Image((ctx.agency as any).logo.url);
    } catch {
      // ignore
    }
  }

  const header = (): any => {
    // Report-specific entries (rebuilt per page — pdfMake mutates header nodes)
    const reportEntries: any[] = [
      {
        text: 'ARREST REPORT',
        style: 'headerCaseInfo',
        margin: [0, 0, 0, 2] as [number, number, number, number],
      },
      {
        text: `Case ID: ${ctx.kase?.number || ''}`,
        style: 'headerCaseInfo',
        margin: [0, 0, 0, 2] as [number, number, number, number],
      },
      {
        text: `Printed on: ${moment(ctx.printedAt).format(DATE_TIME_FORMAT)}`,
        style: 'headerTimestamp',
        margin: [0, 0, 0, 0] as [number, number, number, number],
      },
      {
        text: `Printed by: ${ctx.user?.fullName || ''}`,
        style: 'headerTimestamp',
        margin: [0, 0, 0, 0] as [number, number, number, number],
      },
    ];

    return buildReportHeader({ agency: ctx.agency, logo, reportEntries });
  };

  const footer: any = (currentPage: number, pageCount: number) => ({
    text: `Page ${currentPage} of ${pageCount}`,
    alignment: 'center',
    style: 'footer',
    margin: [0, 10, 0, 0] as [number, number, number, number],
  });

  /**
   * Case Report `createDetailGrid` always lays out N columns per row.
   * For Arrest Report cards we want the special case:
   * if there is ONLY one detail item, it should span the full row width.
   */
  const createDetailGridAuto = (
    fields: Array<{ label: string; value: any }>,
    columnsPerRow = 3,
  ) => {
    if (fields.length === 1) {
      const field = fields[0];
      return {
        stack: [
          {
            columns: [
              {
                text: [
                  { text: `${field.label}: `, style: 'fieldLabel' },
                  field.value,
                ],
                width: '*',
              },
            ],
            columnGap: 10,
            margin: [5, 3, 5, 3] as [number, number, number, number],
          },
        ],
      };
    }
    return createDetailGrid(fields as any, columnsPerRow);
  };

  const valueToContent = (value: any) => {
    return typeof value === 'object' && value !== null && 'text' in value
      ? value
      : { text: safeText(value), style: 'fieldValue' };
  };

  const layoutToFields = (layout: any) => {
    const defs = (layout?.fields ?? []) as any[];
    return defs
      .filter((d) => !d.hidden || !d.hidden(ctx))
      .map((d) => ({
        label: String(d.label || '').replace(/:$/, ''),
        value: valueToContent(d.value(ctx)),
      }));
  };

  const renderSectionHeader = (layout: any) => {
    const fields = layoutToFields(layout);
    if (!fields.length) return { text: '' };
    return createCardHeader(fields as any, layout.columnsPerRow);
  };

  const renderSectionDetail = (layout: any) => {
    const fields = layoutToFields(layout);
    if (!fields.length) return { text: '' };
    return createDetailGridAuto(fields as any, layout.columnsPerRow);
  };

  const content: any[] = [];

  // ADMINISTRATIVE INFORMATION (Case Overview style)
  const admin = arrestReportSections.find(
    (s: any) => s.key === 'administrative',
  );
  if (admin?.detail) {
    content.push(
      createCard(admin.title, renderSectionDetail(admin.detail), true),
    );
  }

  // ARRESTEE PERSONAL INFORMATION (Dispatch style)
  const personal = arrestReportSections.find(
    (s: any) => s.key === 'arresteePersonal',
  );
  if (personal?.detail || personal?.header) {
    const hasHeader = (personal.header?.fields?.length ?? 0) > 0;
    content.push(
      createCard(
        personal.title,
        {
          stack: [
            hasHeader ? renderSectionHeader(personal.header) : { text: '' },
            hasHeader ? createDividerLine() : { text: '' },
            renderSectionDetail(personal.detail),
          ],
        } as any,
        true,
      ),
    );
  }

  // ARREST INFORMATION (Dispatch style)
  const arrestInfo = arrestReportSections.find(
    (s: any) => s.key === 'arrestInfo',
  );
  if (arrestInfo?.detail || arrestInfo?.header) {
    const hasHeader = (arrestInfo.header?.fields?.length ?? 0) > 0;
    content.push(
      createCard(
        arrestInfo.title,
        {
          stack: [
            hasHeader ? renderSectionHeader(arrestInfo.header) : { text: '' },
            hasHeader ? createDividerLine() : { text: '' },
            renderSectionDetail(arrestInfo.detail),
          ],
        } as any,
        true,
      ),
    );
  }

  // OFFENSE DETAILS table (uses template render function)
  const offense = arrestReportSections.find(
    (s: any) => s.key === 'offenseDetails',
  );
  if (offense && offense.render) {
    const tableContent = offense.render(ctx);
    content.push(createCard(offense.title, tableContent, true));
  }

  // ASSOCIATED PERSONS table (uses template render function)
  const assoc = arrestReportSections.find(
    (s: any) => s.key === 'associatedPersons',
  );
  if (assoc && assoc.render) {
    const tableContent = assoc.render(ctx);
    content.push(createCard(assoc.title, tableContent, true));
  }

  // NARRATIVE (Names section style: repeated cards; single-detail field spans full width)
  const narrativeSection = arrestReportSections.find(
    (s: any) => s.key === 'narrative',
  );
  if (narrativeSection) {
    const cards: any[] = [];
    const allNarratives = ctx.narratives || [];

    // Sort narratives: PRIMARY type first, then others sorted by creation date
    const primaryNarrative = allNarratives.find(
      (n: any) => n.type === 'PRIMARY',
    );
    const additionalNarratives = allNarratives
      .filter((n: any) => n.type !== 'PRIMARY')
      .sort((a: any, b: any) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
    const narratives = primaryNarrative
      ? [primaryNarrative, ...additionalNarratives]
      : additionalNarratives;

    if (!narratives.length) {
      content.push(
        createCard(
          narrativeSection.title,
          createDetailGridAuto(
            [
              {
                label: 'Narrative',
                value: { text: FALLBACK, style: 'fieldValue' },
              },
            ] as any,
            3,
          ),
          true,
        ),
      );
    } else {
      narratives.forEach((n: any, idx: number) => {
        const titleData = [
          {
            label: 'Created by',
            value: {
              text: safeText(n?.createdBy?.fullName),
              style: 'fieldValue',
            },
          },
          {
            label: 'Created at',
            value: {
              text: n?.createdAt
                ? moment(n.createdAt).format('MM/DD/YYYY HH:mm')
                : FALLBACK,
              style: 'fieldValue',
            },
          },
        ];

        // Resolve Draft.js raw content (handles object and stringified forms)
        const rawContent = (() => {
          const c = n?.content;
          const r = c?.text ?? c;
          if (typeof r === 'string') {
            try {
              return JSON.parse(r);
            } catch {
              return null;
            }
          }
          return r?.blocks ? r : null;
        })();

        const narrativeBlocks: any[] = rawContent?.blocks
          ? draftjsRawToPdfmakeBlocks(rawContent)
          : [{ text: narrativePlainText(n), style: 'fieldValue' }];

        cards.push({
          table: {
            widths: ['*'],
            body: [
              [
                {
                  stack: [
                    createCardHeader(titleData as any),
                    createDividerLine(),
                    {
                      stack: narrativeBlocks,
                      margin: [5, 3, 5, 3] as [number, number, number, number],
                    },
                  ],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
          },
          margin: [0, 0, 0, 3] as [number, number, number, number],
          pageBreak: idx === 0 ? undefined : undefined,
        } as any);
      });

      // Wrap narrative cards in a borderless inner table; createCard adds the outer border
      const narrativeContent = {
        table: {
          widths: ['*'],
          body: [[{ stack: cards }]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
      };
      content.push(
        createCard(narrativeSection.title, narrativeContent as any, true),
      );
    }
  }

  // SIGNATURES (Dispatch style with image header + SAFIS in detail row)
  const sig = arrestReportSections.find((s: any) => s.key === 'signatures');
  if (sig) {
    const arrestSig = (ctx.arrestee as any).arrestingOfficerSignature as
      | string
      | undefined;
    const supervisorSig = (ctx.arrestee as any).supervisorSignature as
      | string
      | undefined;
    const safis = formatSafisLatentPrint(
      (ctx.arrestee as any).safisLatentPrintIdentificationArrest,
    );

    const sigFields = sig.detail?.fields ?? [];
    const arrestingOfficerIdDef = sigFields.find(
      (f: any) => f.key === 'arrestingOfficerId',
    );
    const supervisorIdDef = sigFields.find(
      (f: any) => f.key === 'supervisorId',
    );

    const arrestingOfficerIdLabel =
      arrestingOfficerIdDef?.label ?? 'Arresting Officer ID Number:';
    const supervisorIdLabel = supervisorIdDef?.label ?? 'Supervisor ID Number:';
    const arrestingOfficerIdValue = arrestingOfficerIdDef
      ? safeText(arrestingOfficerIdDef.value(ctx))
      : FALLBACK;
    const supervisorIdValue = supervisorIdDef
      ? safeText(supervisorIdDef.value(ctx))
      : FALLBACK;

    // Header row: show ID numbers like other sections: "Label: Value"
    const idNumbersRow = {
      columns: [
        {
          text: [
            {
              text: `${String(arrestingOfficerIdLabel).replace(/:$/, '')}: `,
              style: 'fieldLabel',
            },
            { text: arrestingOfficerIdValue, style: 'fieldValue' },
          ],
          width: '*',
        },
        {
          text: [
            {
              text: `${String(supervisorIdLabel).replace(/:$/, '')}: `,
              style: 'fieldLabel',
            },
            { text: supervisorIdValue, style: 'fieldValue' },
          ],
          width: '*',
        },
      ],
      columnGap: 10,
      style: 'cardHeader',
      margin: [5, 3, 5, 3] as [number, number, number, number],
    };

    // Signature row: render signature value directly next to label (not stacked under it)
    const arrestSignatureValue =
      arrestSig && isDataUrlImage(arrestSig)
        ? ({ image: arrestSig, width: 160 } as any)
        : ({ text: FALLBACK, style: 'fieldValue' } as any);
    const supervisorSignatureValue =
      supervisorSig && isDataUrlImage(supervisorSig)
        ? ({ image: supervisorSig, width: 160 } as any)
        : ({ text: FALLBACK, style: 'fieldValue' } as any);

    const signaturesRow = {
      columns: [
        {
          columns: [
            {
              text: 'Arresting Officer Signature: ',
              style: 'fieldLabel',
              width: 'auto',
            },
            // IMPORTANT: don't overwrite image width with '*' (causes pdfMake NaN width error)
            {
              stack: [arrestSignatureValue],
              width: '*',
            },
          ],
          columnGap: 8,
          width: '*',
        },
        {
          columns: [
            {
              text: 'Supervisor Signature: ',
              style: 'fieldLabel',
              width: 'auto',
            },
            // IMPORTANT: don't overwrite image width with '*' (causes pdfMake NaN width error)
            {
              stack: [supervisorSignatureValue],
              width: '*',
            },
          ],
          columnGap: 8,
          width: '*',
        },
      ],
      columnGap: 10,
      margin: [5, 3, 5, 3] as [number, number, number, number],
    };

    const detailData = [
      {
        label:
          'Arrest Made as a Result of A SAFIS Latent Print Identification?',
        value: { text: safis, style: 'fieldValue' },
      },
    ];

    content.push(
      createCard(
        sig.title,
        {
          stack: [
            idNumbersRow,
            signaturesRow,
            createDividerLine(),
            createDetailGridAuto(detailData as any, 3),
          ],
        } as any,
        true,
      ),
    );
  }

  return {
    header,
    footer,
    content,
    styles: pdfStyles,
    pageMargins,
    pageSize: 'A4',
    pageOrientation: 'portrait',
    defaultStyle: {
      fontSize: 10,
      color: '#000',
    },
  } as any;
};
